<template>
  <div>
    <v-container fluid>
      <v-row no-gutters>
        <v-col cols="2">
          <v-menu transition="scale-transition" offset-y v-model="dateMenu">
            <template v-slot:activator="{ on }">
              <v-text-field label="Month" hide-details dense readonly v-on="on" v-model="monthAsDate" outlined append-icon="mdi-menu-down" @click:append="dateMenu=true"/>
            </template>
            <v-date-picker v-model="month" type="month" no-title @change="refresh"/>
          </v-menu>
        </v-col>
        <v-col cols="2">
          <v-select :items="licenses" label="License" v-model="license" @change="refresh" dense hide-details outlined class="ml-6"/>
        </v-col>
        <v-col cols="5" align-self="center" offset="1">
          <reports-excise-file-button :month="month"/>
        </v-col>
        <v-col align-self="center">
          <v-btn outlined small @click="rates()">Excise rates</v-btn>
        </v-col>
        <v-col class="text-right pr-2">
          <v-btn icon @click="printHtml('Excise wine return',report(true))"><v-icon>mdi-printer</v-icon></v-btn>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="mx-4 pa-4 overflow-y" height="calc(100vh - 180px)">
      <span v-html="report(false)"/>
    </v-card>

    <component :is="exciseRates" v-model="showExciseRates"/>

    <v-dialog v-model="showDetails" width="80%">
      <v-card>
        <v-card-title>
          {{title}}
          <v-spacer/>
          <v-icon @click="showDetails=false">mdi-close</v-icon>
        </v-card-title>
        <v-data-table :items="activityReport" disable-pagination disable-filtering disable-sort
                      fixed-header calculate-widths hide-default-footer height="400" class="pa-4"
                      no-data-text="No activity in period"
                      :headers="[{text:'Date',value:'date',width:'15%'},
                                 {text:'Wine',value:'alcoholName',width:'30%'},
                                 {text:'Description',value:'description',width:'40%'},
                                 {text:'Lites',value:'volume',width:'15%',align:'right'}]"
                      @click:row="(e,info) => viewTransaction(info.item)">
          <template v-slot:item.date="{value}">{{value | formatDate}}</template>
          <template v-slot:item.description="{item}">{{item.description}}<div class="text-caption">{{item.comments}}</div></template>
          <template v-slot:item.volume="{value}">{{value*factor | number(3)}}</template>
          <template v-slot:body.append="{headers}">
            <tr :class="darkMode ? 'total-row-dark' : 'total-row'">
              <template v-for="header of headers">
                <td class="text-right" v-if="header.value==='volume'">
                  {{volumeTotal*factor | number(3)}}
                </td>
                <td v-else/>
              </template>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {format,add, lastDayOfMonth} from 'date-fns'
import ReportsExciseFileButton from '@/views/reports/excise/ReportsExciseFileButton'
import printHtml from '@/mixins/printHtml'
import viewTransaction from '@/mixins/viewTransaction'

export default {
  name: 'ReportsExciseWine',
  mixins: [printHtml,viewTransaction],
  components: {ReportsExciseFileButton},
  data() {
    return {
      dateMenu: false,
      month: '',
      license: '',
      exciseWineReport: {header:{},lines:[]},
      exciseRates: null,
      showExciseRates: false,
      showDetails: false,
      title: '',
      excise: null,
      alcoholType: '',
      wineActivity: null,
      exciseTypes: [{excise:'WineAdditionProduced',title:'Wine produced'},
                    {excise:'WineAdditionReceivedFromLicensees',title:'Wine received from wine licensees'},
                    {excise:'WineAdditionReceivedFromLicensedUsers',title:'Wine received from licensed users'},
                    {excise:'WineAdditionImported',title:'Imported wine'},
                    {excise:'WineAdditionPackagedWineReturnedToBulk',title:'Packaged wine returned to bulk wine inventory'},
                    {excise:'WineReductionWineLicensee',title:'Removed for delivery to wine licensees'},
                    {excise:'WineReductionLicensedUsers',title:'Removed for delivery to licensed users'},
                    {excise:'WineReductionExported',title:'Exported wine'},
                    {excise:'WineReductionPackaged',title:'Removed to packaging activities: Packaged'},
                    {excise:'WineReductionPackagedExempt',title:'Removed to packaging activities: Packaged – exempt'},
                    {excise:'WineReductionPackagedMarkedSpecialContainers',title:'Removed to packaging activities: Packaged in marked special containers'},
                    {excise:'WineReductionNonDutyPaid',title:'Wine removed for other purposes: Non-duty-paid'},
                    {excise:'WineReductionDutyPaid',title:'Wine removed for other purposes: Duty-paid'},
                    {excise:'WineInventoryAdjustment',title:'Inventory adjustment'}]
    }
  },
  computed: {
    monthAsDate() {
      return this.$formatDate(this.month+'-01', 'MMMM, yyyy');
    },
    licenses() {
      return [...new Set(this.$store.getters.sites(true, {B265:true}).map(site => site.wineLicense))];
    },
    activityReport() {
      return this.wineActivity?.filter(activity => activity.excise === this.excise && activity.alcoholType === this.alcoholType)?? [];
    },
    volumeTotal() {
      return this.activityReport.reduce((total, activity) => total + activity.volume, 0);
    },
    factor() {
      return this.excise.startsWith('SpiritsReduction') ? -1 : 1;
    }
  },
  async created() {
    this.$emit('name', 'Excise Duty Return - Wine Licensee')
    this.month = this.$route.query.month ?? format(add(new Date(), {months:-1}), 'yyyy-MM');
    this.license = this.licenses[0];
    this.refresh();
  },
  methods: {
    rates() {
      if (!this.exciseRates)
        this.exciseRates = () => import('@/views/reports/excise/ReportsExciseRates');
      this.showExciseRates = true;
    },
    cents(num) {
      let cents = Math.round(num % 1 * 100);
      return cents < 10 ? '0'+cents : cents;
    },
    laa(excise) {
      const entry = this.exciseWineReport.lines.find(line => line.excise === excise);
      return this.$number(entry ? entry.laa : 0, 3);
    },
    // eslint-disable-next-line no-unused-vars
    litres(excise, column) {
      const entry = this.exciseWineReport.lines.find(line => line.excise === excise);
      return entry ? this.$number(entry[column],3) : '0.000';
    },

    async refresh() {
      if (!this.licenses.length) {
        this.$store.commit('setError', {type:'info',message:`There are no Excise Wine licenses`});
        return;
      }

      const results = await this.$apiCall(`query ReportsExciseWine($license:String!,$date:Date!) {
                                            exciseWineReport(license:$license,date:$date) {
                                              line4Litres,line4Excise,line5Litres,line5Excise,line6Litres,line6Excise,excisePayable,
                                              openingInventory,totalAdditions,totalReductions,inventoryAdjustment,closingInventory,
                                              lines{excise,litres1,litres2,litres3},
                                              header {businessName,mailingAddress,city,province,postalCode,
                                                      accountNumber,accountNumberSuffix,
                                                      periodFrom,periodTo,dueDate}
                                            }
                                          }`,
                                          {license:this.license,date:this.month+'-01'});
      if (!results.error)
        this.exciseWineReport = results.data.exciseWineReport;
      this.wineActivity = null;
    },
    async details(excise, alcoholType) {
      if (!this.wineActivity) {
        let to = format(lastDayOfMonth(add(new Date(this.month+'-01'),{days:1})), 'yyyy-MM-dd');
        const results = await this.$apiCall(`query ReportsExciseWine($license:String!,$date:Date!,$to:Date!) {
                                                wineActivity(from:$date,to:$to,userLicense:false,license:$license) {
                                                  date,description,volume,alcoholType,alcoholName,excise,comments,refType,refId
                                                }
                                              }`,
                                            {license:this.license,date:this.month+'-01',to});
        if (!results.error)
          this.wineActivity = results.data.wineActivity;
      }
      this.excise = excise;
      this.alcoholType = alcoholType;
      this.title = this.exciseTypes.find(exciseType => exciseType.excise === excise).title;
      this.showDetails = true;
    },

    report(print) {
      if (!this.licenses.length)
        return '';

      const lines = [
                      `<style>
                        @page {size:portrait}
                        .excise-report {font-family: Roboto, sans-serif; font-size:12px;width:100%;border-collapse:collapse}
                        .excise-report .title {font-weight:700;font-size:18px;text-align:center}
                        .excise-report .sub-title {text-align:center}
                        .excise-report tbody tr {
                          height:30px;
                          padding:8px;
                          vertical-align:middle;
                        }
                        .excise-report .headerRow td {
                          text-align:center;
                          border-left:1px solid #000;
                          border-right:1px solid #000;
                          border-bottom-width:2px;
                        }
                        .excise-report .topRow td {
                          border:1px solid #000;
                          padding:8px;
                        }
                        .excise-report .topRow td:nth-child(1) {
                          width:400px;
                          text-align:left;
                        }
                        .excise-report .topRow td:nth-child(2) {
                          text-align:right;
                          width:100px;
                        }
                        .excise-report .topRow td:nth-child(3) {
                          text-align:center;
                          font-weight:bold;
                          width:5px;
                        }
                        .excise-report .topRow td:nth-child(4) {
                          text-align:right;
                          width:100px;
                        }
                        .excise-report .topRow td:nth-child(5) {
                          text-align:center;
                          width:10px;
                        }
                        .excise-report .topRow td:nth-child(6) {
                          text-align:left;
                          font-weight:bold;
                          border:none;
                          padding-left:10px;
                        }
                        .excise-report .totalRow td {
                          padding:8px;
                        }
                        .excise-report .totalRow td:nth-child(1) {
                          text-align:right;
                          font-weight:bold;
                        }
                        .excise-report .totalRow td:nth-child(2) {
                          text-align:right;
                          width:100px;
                          border:1px solid #000;
                        }
                        .excise-report .totalRow td:nth-child(3) {
                          text-align:center;
                          width:10px;
                          border:1px solid #000;
                        }
                        .excise-report .totalRow td:nth-child(4) {
                          text-align:left;
                          font-weight:bold;
                          width:10px;
                          padding-left:10px;
                        }
                        .excise-report .spacerRow {
                          height:10px;
                        }
                        .excise-report tbody tr:first-child td:nth-child(1) {
                          width:300px;
                        }
                        .excise-report tbody tr:first-child td:nth-child(2) {
                          width:300px;
                        }
                        .excise-report tbody tr:first-child td:nth-child(3) {
                          width:600px;
                        }
                        .excise-report tbody tr:first-child td:nth-child(4) {
                          width:500px;
                        }
                        .excise-report .wineDetail {
                          border:1px solid #000;
                          text-align:right;
                          padding-right:8px;
                          cursor: pointer
                        }
                        .excise-report .wineFirstColumn {
                          border:1px solid #000;
                          padding-left:8px;
                        }
                        .legend {
                          font-weight:bold;
                          padding-left:5px;
                        }
                        .page-break {
                          page-break-before: always;
                        }
                        .page-line {
                          border-top:thin solid #ccc;
                          margin-top: 50px;
                          margin-bottom: 50px;
                        }
                      </style>`,
                    `<table class="excise-report">
                      <thead>
                        <tr>
                          <th colspan="8">
                            <div class="title">Excise Duty Return - Wine Licensee</div>
                          </th>
                        </tr>
                        <tr>
                          <th colspan="8">
                            <div class="sub-title">
                              Account number ${this.exciseWineReport.header.accountNumber||''} RD ${this.exciseWineReport.header.accountNumberSuffix}
                            </div>
                          </th>
                        </tr>
                        <tr>
                          <th colspan="8">
                            <div class="sub-title">
                              ${this.exciseWineReport.header.periodFrom} to ${this.exciseWineReport.header.periodTo}
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td colspan="5" style="border-bottom:1px solid #000"/></tr>
                        <tr class="headerRow">
                          <td><b>Products</b><br/>(according to percentage of absolute ethyl alcohol by volume)</td>
                          <td><b>Quantity</b><br/>(Litres)</td>
                          <td><b>Code</b></td>
                          <td colspan="2"><b>Duty payable</b></td>
                        </tr>
                        <tr class="topRow">
                          <td>Wine containing more than 7%</td>
                          <td>${this.$number(this.exciseWineReport.line4Litres,3)}</td>
                          <td>49570</td>
                          <td>${this.$number(this.exciseWineReport.line4Excise)}</td>
                          <td>4</td>
                        </tr>
                        <tr class="topRow">
                          <td>Wine containing more than 1.2%, but not more than 7%</td>
                          <td>${this.$number(this.exciseWineReport.line5Litres,3)}</td>
                          <td>49569</td>
                          <td>${this.$number(this.exciseWineReport.line5Excise)}</td>
                          <td>5</td>
                        </tr>
                        <tr class="topRow">
                          <td>Wine containing not more than 1.2%</td>
                          <td>${this.$number(this.exciseWineReport.line6Litres,3)}</td>
                          <td>49568</td>
                          <td>${this.$number(this.exciseWineReport.line6Excise)}</td>
                          <td>6</td>
                        </tr>
                        <tr class="totalRow">
                          <td colspan="3">Total (4 to 6) ►</td>
                          <td>${this.$number(this.exciseWineReport.excisePayable)}</td>
                          <td>7</td>
                        </tr>
                        <tr class="spacerRow"><td colspan="8"/>
                        <tr class="totalRow">
                          <td colspan="3">Refunds (B256 attached)</td>
                          <td>0</td>
                          <td>8</td>
                        </tr>
                        <tr class="spacerRow"><td colspan="8"/>
                        <tr class="totalRow">
                          <td colspan="3">Net payable (line 7 minus line 8)</td>
                          <td>${this.$number(this.exciseWineReport.excisePayable)}</td>
                          <td>9</td>
                        </tr>
                        <tr class="spacerRow"><td colspan="8"/>
                        <tr class="totalRow">
                          <td colspan="3">Amount due</td>
                          <td>${this.$number(this.exciseWineReport.excisePayable)}</td>
                          <td>10</td>
                        </tr>
                        <tr class="spacerRow"><td colspan="8"/>
                        <tr class="totalRow">
                          <td colspan="3">Payment / Credit due</td>
                          <td>${this.$number(this.exciseWineReport.excisePayable)}</td>
                          <td>11</td>
                        </tr>
                      </tbody>
                    </table>`,
                    `<div class="${print?'page-break':'page-line'}"></div>`,
                    `<table class="excise-report">
                      <thead>
                        <tr>
                          <th colspan="5">
                            <div class="title">Excise Duty Return - Wine Licensee</div>
                          </th>
                        </tr>
                        <tr>
                          <th colspan="5">
                            <div class="sub-title">
                              Account number ${this.exciseWineReport.header.accountNumber||''} RD ${this.exciseWineReport.header.accountNumberSuffix}
                            </div>
                          </th>
                        </tr>
                        <tr>
                          <th colspan="5">
                            <div class="sub-title">
                              ${this.exciseWineReport.header.periodFrom} to ${this.exciseWineReport.header.periodTo}
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td colspan="5"/>
                        </tr>
                        <tr>
                          <td/>
                          <td colspan="3" style="font-size:120%;text-align:center"><b>Wine</b></td>
                          <td/>
                        </tr>
                        <tr>
                          <td width="30%"/>
                          <td width="22%" style="border:1px solid #666; text-align:center"><b>Containing more than 7%</b><br/>Litres</td>
                          <td width="22%" style="border:1px solid #666; text-align:center"><b>Containing more than<br/>1.2%, but not more than<br/>7%</b><br/>Litres</td>
                          <td width="22%" style="border:1px solid #666; text-align:center"><b>Containing not more<br/>than 1.2%</b><br/>Litres</td>
                          <td/>
                        </tr>
                        <tr>
                          <td style="font-weight:bold; text-align:right; padding-right:8px">Opening inventory ►</td>
                          <td class="wineDetail">${this.$number(this.exciseWineReport.openingInventory?.[0],3)}</td>
                          <td class="wineDetail">${this.$number(this.exciseWineReport.openingInventory?.[1],3)}</td>
                          <td class="wineDetail">${this.$number(this.exciseWineReport.openingInventory?.[2],3)}</td>
                          <td class="legend">A)</td>
                        </tr>
                        <tr>
                          <td colspan="4" class="wineFirstColumn"><b>Additions to bulk wine inventory</b></td>
                        </tr>
                        <tr>
                          <td class="wineFirstColumn">Wine produced</td>
                          <td class="wineDetail" id="WineAdditionProducedWH">${this.litres('WineAdditionProduced','litres1')}</td>
                          <td class="wineDetail" id="WineAdditionProducedWM">${this.litres('WineAdditionProduced','litres2')}</td>
                          <td class="wineDetail" id="WineAdditionProducedWL">${this.litres('WineAdditionProduced','litres3')}</td>
                        </tr>
                        <tr>
                          <td class="wineFirstColumn">Wine received from wine licensees</td>
                          <td class="wineDetail" id="WineAdditionReceivedFromLicenseesWH">${this.litres('WineAdditionReceivedFromLicensees','litres1')}</td>
                          <td class="wineDetail" id="WineAdditionReceivedFromLicenseesWM">${this.litres('WineAdditionReceivedFromLicensees','litres2')}</td>
                          <td class="wineDetail" id="WineAdditionReceivedFromLicenseesWL">${this.litres('WineAdditionReceivedFromLicensees','litres3')}</td>
                        </tr>
                        <tr>
                          <td class="wineFirstColumn">Wine received from licensed users</td>
                          <td class="wineDetail" id="WineAdditionReceivedFromLicensedUsersWH">${this.litres('WineAdditionReceivedFromLicensedUsers','litres1')}</td>
                          <td class="wineDetail" id="WineAdditionReceivedFromLicensedUsersWM">${this.litres('WineAdditionReceivedFromLicensedUsers','litres2')}</td>
                          <td class="wineDetail" id="WineAdditionReceivedFromLicensedUsersWL">${this.litres('WineAdditionReceivedFromLicensedUsers','litres3')}</td>
                        </tr>
                        <tr>
                          <td class="wineFirstColumn">Imported wine</td>
                          <td class="wineDetail" id="WineAdditionImportedWH">${this.litres('WineAdditionImported','litres1')}</td>
                          <td class="wineDetail" id="WineAdditionImportedWM">${this.litres('WineAdditionImported','litres2')}</td>
                          <td class="wineDetail" id="WineAdditionImportedWL">${this.litres('WineAdditionImported','litres3')}</td>
                        </tr>
                        <tr>
                          <td class="wineFirstColumn">Packaged wine returned to bulk wine inventory</td>
                          <td class="wineDetail" id="WineAdditionPackagedWineReturnedToBulkWH">${this.litres('WineAdditionPackagedWineReturnedToBulk','litres1')}</td>
                          <td class="wineDetail" id="WineAdditionPackagedWineReturnedToBulkWM">${this.litres('WineAdditionPackagedWineReturnedToBulk','litres2')}</td>
                          <td class="wineDetail" id="WineAdditionPackagedWineReturnedToBulkWL">${this.litres('WineAdditionPackagedWineReturnedToBulk','litres3')}</td>
                        </tr>
                        <tr>
                          <td style="font-weight:bold; text-align:right; padding-right:8px">Total additions ►</td>
                          <td class="wineDetail">${this.$number(this.exciseWineReport.totalAdditions?.[0],3)}</td>
                          <td class="wineDetail">${this.$number(this.exciseWineReport.totalAdditions?.[1],3)}</td>
                          <td class="wineDetail">${this.$number(this.exciseWineReport.totalAdditions?.[2],3)}</td>
                          <td class="legend">B)</td>
                        </tr>
                        <tr>
                          <td colspan="4" class="wineFirstColumn"><b>Reductions to bulk wine inventory</b></td>
                        </tr>
                        <tr>
                          <td class="wineFirstColumn">Removed for delivery to wine licensees</td>
                          <td class="wineDetail" id="WineReductionWineLicenseeWH">${this.litres('WineReductionWineLicensee','litres1')}</td>
                          <td class="wineDetail" id="WineReductionWineLicenseeWM">${this.litres('WineReductionWineLicensee','litres2')}</td>
                          <td class="wineDetail" id="WineReductionWineLicenseeWL">${this.litres('WineReductionWineLicensee','litres3')}</td>
                        </tr>
                        <tr>
                          <td class="wineFirstColumn">Removed for delivery to licensed users</td>
                          <td class="wineDetail" id="WineReductionLicensedUsersWH">${this.litres('WineReductionLicensedUsers','litres1')}</td>
                          <td class="wineDetail" id="WineReductionLicensedUsersWM">${this.litres('WineReductionLicensedUsers','litres2')}</td>
                          <td class="wineDetail" id="WineReductionLicensedUsersWL">${this.litres('WineReductionLicensedUsers','litres3')}</td>
                        </tr>
                        <tr>
                          <td class="wineFirstColumn">Exported wine</td>
                          <td class="wineDetail" id="WineReductionExportedWH">${this.litres('WineReductionExported','litres1')}</td>
                          <td class="wineDetail" id="WineReductionExportedWM">${this.litres('WineReductionExported','litres2')}</td>
                          <td class="wineDetail" id="WineReductionExportedWL">${this.litres('WineReductionExported','litres3')}</td>
                        </tr>
                        <tr>
                          <td class="wineFirstColumn">Removed to packaging activities: Packaged</td>
                          <td class="wineDetail" id="WineReductionPackagedWH">${this.litres('WineReductionPackaged','litres1')}</td>
                          <td class="wineDetail" id="WineReductionPackagedWM">${this.litres('WineReductionPackaged','litres2')}</td>
                          <td class="wineDetail" id="WineReductionPackagedWL">${this.litres('WineReductionPackaged','litres3')}</td>
                        </tr>
                        <tr>
                          <td class="wineFirstColumn">Removed to packaging activities: Packaged – exempt</td>
                          <td class="wineDetail" id="WineReductionPackagedExemptWH">${this.litres('WineReductionPackagedExempt','litres1')}</td>
                          <td class="wineDetail" id="WineReductionPackagedExemptWM">${this.litres('WineReductionPackagedExempt','litres2')}</td>
                          <td class="wineDetail" id="WineReductionPackagedExemptWL">${this.litres('WineReductionPackagedExempt','litres3')}</td>
                        </tr>
                        <tr>
                          <td class="wineFirstColumn">Removed to packaging activities: Packaged in marked special containers</td>
                          <td class="wineDetail" id="WineReductionPackagedMarkedSpecialContainersWH">${this.litres('WineReductionPackagedMarkedSpecialContainers','litres1')}</td>
                          <td class="wineDetail" id="WineReductionPackagedMarkedSpecialContainersWM">${this.litres('WineReductionPackagedMarkedSpecialContainers','litres2')}</td>
                          <td class="wineDetail" id="WineReductionPackagedMarkedSpecialContainersWL">${this.litres('WineReductionPackagedMarkedSpecialContainers','litres3')}</td>
                        </tr>
                        <tr>
                          <td class="wineFirstColumn">Wine removed for other purposes: Non-duty-paid (specify)</td>
                          <td class="wineDetail" id="WineReductionNonDutyPaidWH">${this.litres('WineReductionNonDutyPaid','litres1')}</td>
                          <td class="wineDetail" id="WineReductionNonDutyPaidWM">${this.litres('WineReductionNonDutyPaid','litres2')}</td>
                          <td class="wineDetail" id="WineReductionNonDutyPaidWL">${this.litres('WineReductionNonDutyPaid','litres3')}</td>
                        </tr>
                        <tr>
                          <td class="wineFirstColumn">Wine removed for other purposes: Duty-paid (specify)</td>
                          <td class="wineDetail" id="WineReductionDutyPaidWH">${this.litres('WineReductionDutyPaid','litres1')}</td>
                          <td class="wineDetail" id="WineReductionDutyPaidWM">${this.litres('WineReductionDutyPaid','litres2')}</td>
                          <td class="wineDetail" id="WineReductionDutyPaidWL">${this.litres('WineReductionDutyPaid','litres3')}</td>
                        </tr>
                        <tr>
                          <td style="font-weight:bold; text-align:right; padding-right:8px">Total reductions ►</td>
                          <td class="wineDetail" id="WineInventoryAdjustmentWH">${this.$number(this.exciseWineReport.totalReductions?.[0],3)}</td>
                          <td class="wineDetail" id="WineInventoryAdjustmentWM">${this.$number(this.exciseWineReport.totalReductions?.[1],3)}</td>
                          <td class="wineDetail" id="WineInventoryAdjustmentWL">${this.$number(this.exciseWineReport.totalReductions?.[2],3)}</td>
                          <td class="legend">C)</td>
                        </tr>
                        <tr>
                          <td colspan="5"/>
                        </tr>
                        <tr>
                          <td style="font-weight:bold; text-align:right; padding-right:8px">Inventory adjustment ( + or – ) ►</td>
                          <td class="wineDetail">${this.$number(this.exciseWineReport.inventoryAdjustment?.[0],3)}</td>
                          <td class="wineDetail">${this.$number(this.exciseWineReport.inventoryAdjustment?.[1],3)}</td>
                          <td class="wineDetail">${this.$number(this.exciseWineReport.inventoryAdjustment?.[2],3)}</td>
                          <td class="legend">D)</td>
                        </tr>
                        <tr>
                          <td colspan="5"/>
                        </tr>
                        <tr>
                          <td style="font-weight:bold; text-align:right; padding-right:8px">Closing inventory (A + B) – C ± D ►</td>
                          <td class="wineDetail">${this.$number(this.exciseWineReport.closingInventory?.[0],3)}</td>
                          <td class="wineDetail">${this.$number(this.exciseWineReport.closingInventory?.[1],3)}</td>
                          <td class="wineDetail">${this.$number(this.exciseWineReport.closingInventory?.[2],3)}</td>
                          <td class="legend">E)</td>
                        </tr>
                      </tbody>
                    </table>`
      ];

      if (!print)
        this.$nextTick( () =>
          this.exciseTypes.forEach(exciseType => {
            document.getElementById(`${exciseType.excise}WL`).addEventListener("click", () => this.details(exciseType.excise, 'WineWeak'));
            document.getElementById(`${exciseType.excise}WM`).addEventListener("click", () => this.details(exciseType.excise, 'WineMedium'));
            document.getElementById(`${exciseType.excise}WH`).addEventListener("click", () => this.details(exciseType.excise, 'WineHigh'));
          })
        )

      return lines.join('');
    }
  }
}
</script>